import {
  Alert,
  Badge,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  SxProps,
  Tab,
  Tabs,
} from '@mui/material'
import { useEffect, useState, useCallback, Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { notificationApi } from 'hooks/api/notification'
import {
  Notification,
  contentType,
  notificationStatus,
  getBadgeStyle,
} from 'types/notification'
import { PRIMARY_COLOR } from 'valiable'

export default function NotificationPage(): JSX.Element {
  const { t } = useTranslation(['page-notification', 'common'])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isUnread, setIsUnread] = useState<boolean>(true)
  const [selectedTab, setSelectedTab] = useState<number>(0)

  const fetchData = useCallback(() => {
    notificationApi()
      .getAll(isUnread)
      .then((res) => {
        setNotifications(res.data)
      })
  }, [isUnread])

  useEffect(() => {
    fetchData()
  }, [isUnread, fetchData])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setIsUnread(newValue === 0) // 未読が0, 全てが1
  }

  const handleListItemClick = async (notification: Notification) => {
    console.log(notification.contentType)
    switch (notification.contentType) {
      case contentType.AssignConfirmation: // 勤務指示への回答
        location.href = `/notification/${notification.id}/assign-confirm?slide=right`
        break
      case contentType.AssignSuspension: // 中止
        location.href = `/notification/${notification.id}/assign-suspention?slide=right`
        break
      case contentType.AssignNotification: // お知らせ
        location.href = `/notification/${notification.id}/assign-notification?slide=right`
        break
      case contentType.Announcement: // 社内報
        location.href = `/notification/${notification.id}/announcement?slide=right`
        break
      case contentType.ThoughtfulInterviewsVER_1: // 警備員への思考性アンケート
        location.href = `/notification/${notification.id}/interviews?slide=right`
        break
      case contentType.SecurityInsight: // システム通知[kumocan -> 警備員に送る検証用の質問]
        location.href = `/notification/${notification.id}/insight?slide=right`
        break
      case contentType.SecurityReport: {
        // 警備報告書
        const res = await handleReadSecurityReport(notification)
        location.href = `/security-report?contract_detail_id=${res.data.contractDetailId}&slide=right`
        break
      }
      case contentType.SecuriryOnboarding_V1: {
        await notificationApi().readed(notification.id)
        location.href = `/insight/onboarding-questionnaire?slide=right`
        break
      }
      default:
        console.log('Notification status is not recognized.')
    }
  }

  const handleReadSecurityReport = async (notification: Notification) => {
    await notificationApi().readed(notification.id)
    return await notificationApi().findSecurityReport(notification.id)
  }

  const notificationLabel = (noti: Notification): string => {
    switch (noti.contentType) {
      case contentType.AssignSuspension:
        return t('labels.suspension')
      case contentType.ThoughtfulInterviewsVER_1:
        return t('labels.important')
      case contentType.Announcement:
      case contentType.AssignNotification:
      case contentType.SecurityInsight:
        return t('labels.announcement')
      case contentType.SecuriryOnboarding_V1:
        return t('labels.announcement')
      default:
        return t(
          `common:badge_types.${noti.shiftType}`,
          t('common:badge_types.unknown'),
        )
    }
  }

  return (
    <Box>
      <Box sx={{ width: '100%', marginTop: '10px' }}>
        <Box
          sx={{
            width: '100%',
            borderBottom: `2px solid ${PRIMARY_COLOR}`, // 画面全体に下線を引く
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: '80%',
              margin: '0 auto',
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  backgroundColor: '#e6e6e6',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  borderRadius: '10px 10px 0 0',
                  minHeight: '34px',
                  height: '34px',
                  color: 'grey !important',
                },
                borderRadius: '10px 10px 0 0',
                '& .Mui-selected': {
                  backgroundColor: PRIMARY_COLOR,
                  borderRadius: '10px 10px 0 0',
                  color: 'white !important',
                },
                minHeight: '34px',
              }}
              TabIndicatorProps={{
                sx: { display: 'none' }, // 標準の下線は非表示
              }}
              variant="fullWidth"
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span>{t('tabs.unread')}</span>
                    <Badge
                      badgeContent={
                        notifications.filter(
                          (n) => n.status !== notificationStatus.AlreadyRead,
                        ).length
                      }
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: 'red',
                          color: 'white',
                          right: '-20px',
                        },
                      }}
                      max={99}
                    />
                  </Box>
                }
              />
              <Tab label={t('tabs.all')} sx={{ marginLeft: '10px' }} />
            </Tabs>
          </Box>
        </Box>
      </Box>

      {notifications.length === 0 && (
        <Alert
          severity="info"
          sx={{
            margin: '10px auto 0',
            width: '80%',
          }}
        >
          {t('empty_message')}
        </Alert>
      )}
      <List>
        {notifications.map((notification, index) => (
          <Fragment key={notification.id}>
            <ListItemButton
              key={notification.id}
              onClick={() => handleListItemClick(notification)}
            >
              <Box sx={notificationStyle(notification)}>
                {notificationLabel(notification)}
              </Box>

              <ListItemText
                primaryTypographyProps={{
                  sx: {
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color:
                      notification.contentType === contentType.AssignSuspension
                        ? 'red'
                        : '#666666',
                  },
                }}
                primary={notification.title}
                secondary={`${t('sent_at_label')} ${notification.sentAt.format('YYYY-MM-DD HH:mm')}`}
                sx={{ marginLeft: '12px', width: '90%' }}
              />

              {notification.status !== notificationStatus.AlreadyRead && (
                <Box
                  sx={{
                    width: '15px',
                    height: '15px',
                    backgroundColor: 'red',
                    borderRadius: '50%',
                  }}
                />
              )}
            </ListItemButton>

            {index !== notifications.length - 1 && <Divider />}
          </Fragment>
        ))}
      </List>
    </Box>
  )
}

const notificationColor = (notif: Notification): string => {
  switch (notif.contentType) {
    case contentType.AssignSuspension:
    case contentType.ThoughtfulInterviewsVER_1:
      return '#FF4081'
    case contentType.Announcement:
    case contentType.AssignNotification:
    case contentType.SecurityInsight:
      return '#C0C0C0'
    default:
      return getBadgeStyle(notif.shiftType).backgroundColor ?? ''
  }
}

const notificationStyle = (notif: Notification): SxProps => ({
  minWidth: '50px',
  minHeight: '45px',
  display: 'flex',
  justifyContent: 'center',
  fontWeight: 'bold',
  alignItems: 'center',
  borderRadius: '4px',
  backgroundColor: notificationColor(notif),
  color: 'white',
  padding: '4px 6px',
  flexShrink: 0,
})
